/**
 * Bluesky data layer — PUBLIC, READ-ONLY, NO LOGIN.
 *
 * We hit the public AppView API (no auth token, no OAuth, no password, no
 * permissions granted) to turn a handle into the avatars of that account's
 * MUTUALS (people who follow each other). Those avatars get painted onto the
 * enemy tanks. Because it's all unauthenticated reads of already-public data,
 * there's nothing sketchy to consent to — matching the project's stance.
 *
 * (Writing stats into the user's PDS is a separate, later step that DOES need
 * OAuth; that's the only piece that ever asks for permission, and it's
 * read+own-collection-write only — never post creation.)
 *
 * Adapted from the bskyrts project's bsky.js.
 */

const API = "https://public.api.bsky.app/xrpc";
const FOLLOW_PAGES = 50; // paginate until the cursor runs out (≤5000 follows — sanity cap)
const REL_BATCH = 30; // getRelationships caps `others` at 30

async function xrpc<T>(method: string, params: Record<string, string | string[]>): Promise<T> {
  const url = new URL(`${API}/${method}`);
  for (const [k, v] of Object.entries(params)) {
    if (Array.isArray(v)) v.forEach((x) => url.searchParams.append(k, x));
    else url.searchParams.set(k, v);
  }
  const res = await fetch(url, { headers: { accept: "application/json" } });
  if (!res.ok) throw new Error(`${method} ${res.status}`);
  return res.json() as Promise<T>;
}

interface Follow {
  did: string;
  handle?: string;
  displayName?: string;
  avatar?: string;
}

/**
 * Same-origin image proxy URL. The Bluesky CDN serves avatars WITHOUT CORS
 * headers, so a WebGL texture upload from them would taint the canvas / fail.
 * Re-serving through our own /img route (worker in prod, vite plugin in dev)
 * keeps the draw same-origin.
 */
export function proxiedImage(url: string): string {
  return `/img?u=${encodeURIComponent(url)}`;
}

export interface MutualFaceRef {
  /** proxied avatar URL ready for THREE.TextureLoader */
  url: string;
  /** display name (account name), falling back to handle */
  name: string;
}

/** A loaded mutual avatar: a THREE texture + the account's display name. */
export interface AvatarFace {
  texture: import("three").Texture;
  name: string;
}

export interface MutualResult {
  handle: string;
  did: string;
  /** the player's own display name (account name), falling back to handle */
  selfName: string;
  faces: MutualFaceRef[];
  mutualCount: number;
}

/**
 * Resolve a handle to its mutuals' avatar URLs. Best-effort and resilient:
 * partial failures just yield fewer avatars rather than throwing.
 */
export async function fetchMutualFaces(
  rawHandle: string,
  onProgress?: (msg: string) => void,
  want = 250, // every mutual becomes a zombie face (capped for GPU sanity)
): Promise<MutualResult> {
  const log = onProgress ?? (() => {});
  const handle = rawHandle.trim().replace(/^@/, "");
  log(`Resolving @${handle}…`);
  const { did } = await xrpc<{ did: string }>("com.atproto.identity.resolveHandle", { handle });

  // the player's own display name (account name) for the leaderboard / name tag
  let selfName = handle;
  try {
    const prof = await xrpc<{ displayName?: string }>("app.bsky.actor.getProfile", { actor: did });
    if (prof.displayName) selfName = prof.displayName;
  } catch {
    /* keep handle */
  }

  log("Scanning who they follow…");
  const follows: Follow[] = [];
  let cursor: string | undefined;
  for (let p = 0; p < FOLLOW_PAGES; p++) {
    const data = await xrpc<{ follows: Follow[]; cursor?: string }>("app.bsky.graph.getFollows", {
      actor: did,
      limit: "100",
      ...(cursor ? { cursor } : {}),
    });
    for (const f of data.follows ?? []) if (f.avatar) follows.push(f);
    cursor = data.cursor;
    if (!cursor) break;
    if (p > 0 && p % 5 === 0) log(`Scanning who they follow… ${follows.length} so far`);
  }

  log(`${follows.length} follows · checking who follows back…`);
  const byDid = new Map(follows.map((f) => [f.did, f]));
  const mutualDids: string[] = [];
  for (let i = 0; i < follows.length; i += REL_BATCH) {
    if (i > 0 && i % 300 === 0) log(`Checking follow-backs… ${i}/${follows.length}`);
    const batch = follows.slice(i, i + REL_BATCH);
    try {
      const data = await xrpc<{ relationships: Array<{ did: string; followedBy?: boolean }> }>(
        "app.bsky.graph.getRelationships",
        { actor: did, others: batch.map((f) => f.did) },
      );
      for (const r of data.relationships ?? []) if (r.followedBy) mutualDids.push(r.did);
    } catch {
      /* skip batch */
    }
  }

  log(`${mutualDids.length} mutuals · loading faces…`);
  const faces: MutualFaceRef[] = [];
  for (const d of mutualDids) {
    const f = byDid.get(d);
    if (f?.avatar) faces.push({ url: proxiedImage(f.avatar), name: f.displayName || f.handle || "mutual" });
    if (faces.length >= want) break;
  }

  return { handle, did, selfName, faces, mutualCount: mutualDids.length };
}

/** Load face refs into THREE textures paired with their handle (skips failures). */
export async function loadFaces(
  refs: MutualFaceRef[],
  THREE: typeof import("three"),
): Promise<Array<{ texture: import("three").Texture; name: string }>> {
  const loader = new THREE.TextureLoader();
  const out: Array<{ texture: import("three").Texture; name: string }> = [];
  await Promise.all(
    refs.map(
      (ref) =>
        new Promise<void>((resolve) => {
          loader.load(
            ref.url,
            (tex) => {
              tex.colorSpace = THREE.SRGBColorSpace;
              out.push({ texture: tex, name: ref.name });
              resolve();
            },
            undefined,
            () => resolve(),
          );
        }),
    ),
  );
  return out;
}
